import { logError, logInfo } from './logging';
import { SelectionSlot } from '../context/personalisation-typedefs';

const IMAGE_PROPERTY_KEY_FLAGS = ['visual', 'original', 'overlay', 'inside left', 'inside right'];
const EXCEPTION_LIST = ['_original_'];

/**
 * Checks if a URL is a Shopify CDN URL
 */
const isShopifyUrl = (url: unknown): boolean => {
  if (typeof url !== 'string') {
    return false;
  }
  return url.includes('cdn.shopify.com') || url.includes('shopifycdn.com');
};

/**
 * Normalizes a property key by converting " - Visual" to " Visual" (matching addImageUrls.js behavior)
 */
const normalizeKeyForBaseAttributes = (key: string): string => {
  return key.replace(' - Visual', ' Visual');
};

/**
 * Extracts property key names from FormData keys
 * @example 'items[0][properties][key]' -> 'key'
 */
export const extractFormDataPropertyKeys = (formData: FormData, itemIndex: number = 0): string[] => {
  const formPropertyKeys = Array.from(formData.keys()).filter(
    (key) => key.startsWith(`items[${itemIndex}][properties][`) && key.endsWith(']')
  );

  return formPropertyKeys
    .map((key) => {
      const match = key.match(new RegExp(`^items\\[${itemIndex}\\]\\[properties\\]\\[(.+)\\]$`));
      return match ? match[1] : null;
    })
    .filter((key): key is string => key !== null);
};

/**
 * Extracts image property keys from FormData that are expected to have Shopify URLs
 */
const extractImagePropertyKeysFromFormData = (formData: FormData, itemIndex: number = 0): string[] => {
  const propertyKeys = extractFormDataPropertyKeys(formData, itemIndex);

  return propertyKeys.filter((key) => {
    // Check if this is an image-related property
    return (
      key.endsWith(' - Visual') ||
      key.endsWith(' Visual') ||
      key.includes('Visual') ||
      key.includes('Original') ||
      key.includes('Overlay') ||
      key.includes('inside left') ||
      key.includes('inside right')
    );
  });
};

/**
 * Extracts File objects from FormData for image properties
 * Returns a Map of property key to File object
 */
export const extractFilesFromFormData = (formData: FormData, itemIndex: number = 0): Map<string, File> => {
  const fileMap = new Map<string, File>();
  const imagePropertyKeys = extractImagePropertyKeysFromFormData(formData, itemIndex);

  imagePropertyKeys.forEach((propertyKey) => {
    const formDataKey = `items[${itemIndex}][properties][${propertyKey}]`;
    const value = formData.get(formDataKey);

    // Check if the value is a File object
    if (value instanceof File) {
      fileMap.set(propertyKey, value);
    }
  });

  return fileMap;
};

/**
 * Extracts Shopify URLs from data item properties
 */
const extractShopifyUrlsFromDataProperties = (properties: Record<string, unknown>): Map<string, string> => {
  const urlMap = new Map<string, string>();

  if (!properties || typeof properties !== 'object') {
    return urlMap;
  }

  Object.entries(properties).forEach(([key, value]) => {
    // Skip non-image properties
    if (
      key === '_personalisation_choices' ||
      key === '_personalisation_base_attributes' ||
      key.startsWith('_featured_image') ||
      !isShopifyUrl(value)
    ) {
      return;
    }

    // Check if this is an image-related property
    if (
      key.endsWith(' - Visual') ||
      key.includes('Visual') ||
      key.includes('Original') ||
      key.includes('Overlay') ||
      key.includes('inside left') ||
      key.includes('inside right')
    ) {
      urlMap.set(key, value as string);
    }
  });

  return urlMap;
};

/**
 * Extracts Shopify URLs from newPayload base attributes
 */
const extractShopifyUrlsFromBaseAttributes = (baseAttributes: Record<string, unknown>): Map<string, string> => {
  const urlMap = new Map<string, string>();

  if (!baseAttributes || typeof baseAttributes !== 'object') {
    return urlMap;
  }

  Object.entries(baseAttributes).forEach(([key, value]) => {
    if (isShopifyUrl(value)) {
      urlMap.set(key, value as string);
    }
  });

  return urlMap;
};

export const validateAddCartFromData = ({
  formData,
  items,
  slots
}: {
  formData: FormData;
  items: any[];
  slots: SelectionSlot[];
}) => {
  try {
    // If its a personalised product (check properties._personalisation_choices), check we have
    // populated the base attributes with the expected line item properties.
    const choices = formData.get('items[0][properties][_personalisation_choices]');

    if (choices !== '[]' && choices !== null && items[0]) {
      const addedItem = items[0];
      const { properties, id } = addedItem;

      // Extract the property key names from FormData keys (e.g., 'items[0][properties][key]' -> 'key').
      const formPropertyKeyNames = extractFormDataPropertyKeys(formData, 0);

      // Check the base attributes for the line item properties we're expecting.
      // Type guard to ensure we only process slots with lineItemPropertyKey.
      const expectedBaseLineItemPropertyKeys = slots
        .filter(
          (slot): slot is SelectionSlot =>
            slot != null &&
            typeof slot === 'object' &&
            'lineItemPropertyKey' in slot &&
            typeof slot.lineItemPropertyKey === 'string' &&
            // slot selected can be empty if not required, but if required, it must be populated.
            (slot.isRequired === true || (slot.isRequired === false && slot.selected !== null))
        )
        .map((slot) => slot.lineItemPropertyKey);

      // Check if expected image properties exist in FormData.
      const includedImageProperties = expectedBaseLineItemPropertyKeys
        // We're just checking for LIPs that are used to upload images.
        .filter((lineItemPropertyKey) =>
          IMAGE_PROPERTY_KEY_FLAGS.some((fieldFlag) =>
            lineItemPropertyKey?.toLowerCase().includes(fieldFlag.toLowerCase())
          )
        );

      // Check the form data for the base line item properties for images.
      const missingImagePropertiesInFormData = includedImageProperties.filter(
        (lineItemPropertyKey) => !formPropertyKeyNames.includes(lineItemPropertyKey)
      );

      // Also check the data properties for the lips that are added via logic - we do this just by checking the list of form property key names.
      const includedImagePropertiesInData = formPropertyKeyNames.filter((key) =>
        IMAGE_PROPERTY_KEY_FLAGS.some((fieldFlag) => key.toLowerCase().includes(fieldFlag.toLowerCase()))
      );

      // Check if any of the lips we've gathered are empty.
      const emptyImageLips = [...includedImageProperties, ...includedImagePropertiesInData]
        .filter((key): key is string => key !== null)
        // Remove any keys that are in the exception list.
        .filter((key) => {
          if (EXCEPTION_LIST.includes(key)) {
            return false;
          }
          const value = formData.get(`items[0][properties][${key}]`);
          return value === null || value === '' || typeof value === 'undefined';
        });

      if (missingImagePropertiesInFormData.length > 0) {
        logError({
          label: 'addToCart - missing image LIPs',
          error: new Error('Missing image line item properties for personalised product'),
          message: 'Missing image line item properties in FormData',
          extraData: {
            missingImagePropertiesInFormData,
            baseAttributes: properties?._personalisation_base_attributes,
            baseProductId: id,
            properties,
            formPropertyKeyNames
          },
          tags: {
            lips: 'missing',
            productId: `${id}`
          }
        });
      } else if (emptyImageLips.length > 0) {
        logError({
          label: 'addToCart - empty image LIPs',
          error: new Error('Empty image line item properties for personalised product'),
          message: 'Empty image line item properties in FormData',
          extraData: {
            emptyImageLips,
            baseAttributes: properties?._personalisation_base_attributes,
            baseProductId: id,
            properties,
            formPropertyKeyNames,
            includedImageProperties,
            includedImagePropertiesInData
          }
        });
      } else {
        logInfo({
          label: 'addToCart - image LIPs present',
          message: 'Image line item properties present in FormData',
          extraData: {
            formPropertyKeyNames,
            includedImageProperties
          }
        });
      }
    }
  } catch (error) {
    // Silently log the error and continue as this is not a critical error.
    logError({
      label: 'validateAddCartFromData',
      error,
      message: 'Error validating add cart from data',
      extraData: { formData, items, slots }
    });
  }
};

export const validateChangeCartData = ({
  newPayload,
  cartItems,
  formData
}: {
  newPayload: any;
  cartItems: any;
  formData: FormData;
}): { requiresReAddingProduct: boolean; id: string | null } => {
  try {
    // Find the item that has just been added to the cart.
    const addedItem = cartItems?.find((item: any) => {
      return newPayload?.id?.startsWith(item.id);
    });
    if (!addedItem || !addedItem.properties) {
      throw new Error('Added item not found in data');
    }

    // Extract File objects from FormData for image properties
    const formDataFiles = extractFilesFromFormData(formData, 0);

    // Extract base attributes from newPayload
    const baseAttributes = newPayload?.properties?._personalisation_base_attributes;
    const newPayloadShopifyUrls = extractShopifyUrlsFromBaseAttributes(
      typeof baseAttributes === 'object' ? baseAttributes : {}
    );

    const { requiresReAddingProduct: requiresReAddingProductCartHasImages, id: idCartHasImages } =
      validateCartHasImages({ formDataFiles, addedItem });

    if (requiresReAddingProductCartHasImages) {
      return { requiresReAddingProduct: true, id: idCartHasImages };
    }

    // Check if newPayload has Shopify URLs for all Files from formData
    const missingUrlsInNewPayload: string[] = [];
    const keysWithoutUrlsInNewPayload: string[] = [];

    formDataFiles.forEach((file, propertyKey) => {
      // Normalize the key to match base attributes format (e.g., " - Visual" -> " Visual")
      const normalizedKey = normalizeKeyForBaseAttributes(propertyKey);
      const hasUrlInNewPayload = newPayloadShopifyUrls.has(normalizedKey);

      if (!hasUrlInNewPayload) {
        // Check if the key exists at all in base attributes (even without a URL)
        const keyExistsInNewPayload = baseAttributes && normalizedKey in baseAttributes;

        if (keyExistsInNewPayload) {
          keysWithoutUrlsInNewPayload.push(propertyKey);
        } else {
          missingUrlsInNewPayload.push(propertyKey);
        }
      }
    });

    if (missingUrlsInNewPayload.length > 0 || keysWithoutUrlsInNewPayload.length > 0) {
      logError({
        label: 'validateChangeCartData - missing Shopify URLs in newPayload',
        error: new Error('Missing Shopify URLs in newPayload base attributes for Files from FormData'),
        message: 'NewPayload base attributes do not contain Shopify URLs for all Files from FormData',
        extraData: {
          missingUrlsInNewPayload,
          keysWithoutUrlsInNewPayload,
          formDataFiles: Array.from(formDataFiles.keys()).map((key) => ({
            propertyKey: key,
            fileName: formDataFiles.get(key)?.name,
            fileSize: formDataFiles.get(key)?.size,
            fileType: formDataFiles.get(key)?.type
          })),
          newPayloadShopifyUrls: Object.fromEntries(newPayloadShopifyUrls),
          baseAttributes,
          itemId: addedItem.id,
          itemKey: addedItem.key
        },
        tags: {
          validation: 'missing_urls_newpayload',
          itemId: `${addedItem.id}`
        }
      });
      return { requiresReAddingProduct: true, id: addedItem.id };
    }
    return { requiresReAddingProduct: false, id: null };
  } catch (error) {
    // Silently log the error and continue as this is not a critical error.
    logError({
      label: 'validateChangeCartData',
      error,
      message: 'Error validating change cart data',
      extraData: { newPayload, cartItems, formData }
    });
    return { requiresReAddingProduct: true, id: null };
  }
};

export const validateCartHasImages = ({
  formDataFiles,
  addedItem
}: {
  formDataFiles: Map<string, File>;
  addedItem: any;
}): { requiresReAddingProduct: boolean; id: string | null; key: string | null } => {
  // Extract Shopify URLs from data properties
  const dataShopifyUrls = extractShopifyUrlsFromDataProperties(addedItem.properties);
  // Check if data has Shopify URLs for all Files from formData
  const missingUrlsInData: string[] = [];
  const keysWithoutUrlsInData: string[] = [];

  formDataFiles.forEach((file, propertyKey) => {
    // Check if this key exists in data properties with a Shopify URL
    const hasUrlInData = dataShopifyUrls.has(propertyKey);

    if (!hasUrlInData) {
      // Check if the key exists at all in data properties (even without a URL)
      const keyExistsInData = propertyKey in addedItem.properties;

      if (keyExistsInData) {
        keysWithoutUrlsInData.push(propertyKey);
      } else {
        missingUrlsInData.push(propertyKey);
      }
    }
  });

  // Log errors if the Shopify URLs are missing in the data - and return true if we need to re-add the product.
  if (missingUrlsInData.length > 0 || keysWithoutUrlsInData.length > 0) {
    logError({
      label: 'validateChangeCartData - missing Shopify URLs in data',
      error: new Error('Missing Shopify URLs in cart data for Files from FormData'),
      message: 'Cart data does not contain Shopify URLs for all Files from FormData',
      extraData: {
        missingUrlsInData,
        keysWithoutUrlsInData,
        formDataFiles: Array.from(formDataFiles.keys()).map((key) => ({
          propertyKey: key,
          fileName: formDataFiles.get(key)?.name,
          fileSize: formDataFiles.get(key)?.size,
          fileType: formDataFiles.get(key)?.type
        })),
        dataShopifyUrls: Object.fromEntries(dataShopifyUrls),
        itemId: addedItem.id,
        itemKey: addedItem.key
      },
      tags: {
        validation: 'missing_urls_data',
        itemId: `${addedItem.id}`
      }
    });
    return { requiresReAddingProduct: true, id: addedItem.id, key: addedItem.key };
  }
  return { requiresReAddingProduct: false, id: null };
};
